import React, { useRef, useEffect, useState } from "react";
import { TableCell, Tooltip } from "@mui/material";

interface EllipsisTableCellProps {
  children: React.ReactNode;
  maxWidth?: number | string;
  style?: React.CSSProperties;
  className?: string;
  title?: string;
  tooltipPlacement?:
  | "top"
  | "bottom"
  | "left"
  | "right"
  | "top-start"
  | "top-end"
  | "bottom-start"
  | "bottom-end";
  enableEllipsis?: boolean;
  enableTooltip?: boolean;
  allowMultiline?: boolean;
  [key: string]: any; // For other TableCell props
}

const EllipsisTableCell: React.FC<EllipsisTableCellProps> = ({
  children,
  maxWidth = 200,
  style = {},
  className = "",
  title,
  tooltipPlacement = "top",
  enableEllipsis = true,
  enableTooltip = true,
  allowMultiline = false,
  ...otherProps
}) => {
  const cellRef = useRef<HTMLTableCellElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [tooltipContent, setTooltipContent] = useState("");

  // Check if content is overflowing (skip when ellipsis/tooltip disabled or multiline)
  useEffect(() => {
    if (!enableEllipsis || !enableTooltip || allowMultiline) return;
    const checkOverflow = () => {
      if (contentRef.current) {
        const element = contentRef.current;
        const isTextOverflowing = element.scrollWidth > element.clientWidth;
        setIsOverflowing(isTextOverflowing);

        // Set tooltip content based on the actual text content
        if (isTextOverflowing) {
          const textContent = element.textContent || element.innerText || "";
          setTooltipContent(textContent);
        }
      }
    };

    checkOverflow();

    const resizeObserver = new ResizeObserver(checkOverflow);
    if (contentRef.current) {
      resizeObserver.observe(contentRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [children, allowMultiline, enableEllipsis, enableTooltip]);

  const cellStyle: React.CSSProperties = {
    maxWidth,
    ...style,
  };

  const useEllipsisLayout = enableEllipsis && !allowMultiline;

  const contentStyle: React.CSSProperties = !useEllipsisLayout
    ? {
      whiteSpace: "pre-line",
      overflow: "visible",
      width: "100%",
      display: "block",
    }
    : {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      width: "100%",
      display: "block",
    };

  const cellContent = (
    <div ref={contentRef} style={contentStyle} className="ellipsis-content">
      {children}
    </div>
  );

  // Empty title blocks ancestor row `title` tooltips (e.g. transfer validation errors on link cells).
  const nativeTitle = enableTooltip ? title : "";

  // If tooltip is disabled, allowMultiline, or content is not overflowing, return cell without tooltip
  if (!enableTooltip || allowMultiline || !isOverflowing) {
    return (
      <TableCell
        ref={cellRef}
        style={cellStyle}
        className={`ellipsis-table-cell ${className}`}
        title={nativeTitle}
        {...otherProps}
      >
        {cellContent}
      </TableCell>
    );
  }

  // Return cell with tooltip for overflowing content
  return (
    <TableCell
      ref={cellRef}
      style={cellStyle}
      className={`ellipsis-table-cell ${className}`}
      {...otherProps}
    >
      <Tooltip
        title={tooltipContent || title || ""}
        placement={tooltipPlacement}
        arrow
        enterDelay={500}
        leaveDelay={200}
        componentsProps={{
          tooltip: {
            sx: {
              bgcolor: "#333",
              color: "#fff",
              fontSize: "0.875rem",
              maxWidth: 300,
              padding: "8px 12px",
              borderRadius: "4px",
              boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
              "& .MuiTooltip-arrow": {
                color: "#333",
              },
            },
          },
        }}
      >
        {cellContent}
      </Tooltip>
    </TableCell>
  );
};

export default EllipsisTableCell;
